import * as React from "react";
import { useEffect, useState, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { axiosRequest, RequestType } from "../../utils/network";
import { AxiosResponse } from "axios";

interface Asset {
  code: string;
  teller_code: string;
  name: string;
  icon_url: string;
}

interface Wallet {
  id: string;
  asset: Asset;
  balance: string;
  base_balance: number;
}

interface LedgerEntry {
  id: string;
  date: string;
  description: string;
  isPositive: boolean;
  amount: string;
  link: string;
  linkText: string;
}

interface Distribution {
  id: string;
  state: string;
  vesting_date: string;
  amount: {
    cents: number;
    currency_iso: string;
  };
}

interface Eligibilities {
  can_withdraw: boolean;
  can_deposit: boolean;
  can_trade: boolean;
}

const useStyles = makeStyles(() => ({
  page: {
    backgroundColor: "#F9FAFB",
    fontFamily: "Inter, sans-serif",
    display: "flex",
    justifyContent: "center",
  },
  container: {
    width: "100%",
    maxWidth: "1200px",
    padding: "0 24px",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
    boxSizing: "border-box",
  },
  balanceHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  balanceLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: "#6B7280",
    marginBottom: -12,
  },
  h1: {
    fontSize: "65px",
    fontWeight: 800,
    color: "#111827",
  },
  actionRow: {
    display: "flex",
    gap: "12px",
  },
  actionBtn: {
    backgroundColor: "#111827",
    color: "#FFF",
    padding: "10px 18px",
    borderRadius: "24px",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    border: "none",
    minWidth: "120px",
    '&:hover': {
      backgroundColor: "#1F2937"
    },
    '&:disabled': {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },

  lightBtn: {
    backgroundColor: "#F3F4F6",
    color: "#111827",
    padding: "10px 18px",
    borderRadius: "24px",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    border: "none",
    minWidth: "120px",
    '&:hover': {
      backgroundColor: "#111827",
      color: "#FFFFFF",
      border: "1px solid #111827",
    },
    '&:disabled': {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },
  searchBox: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #D1D5DB",
    fontSize: 14,
    width: "100%",
    maxWidth: "400px",
  },
  assetScrollWrapper: {
    overflow: "hidden",
    display: "grid",
  },
  assetScroll: {
    display: "flex",
    gap: 16,
    overflowX: "auto",
    paddingTop: "12px",
    paddingBottom: "12px",
    scrollSnapType: "x mandatory",
    WebkitOverflowScrolling: "touch",
    boxSizing: "border-box",
  },
  assetCard: {
    minWidth: 160,
    maxWidth: 180,
    scrollSnapAlign: "start",
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    cursor: "pointer",
    border: "1px solid #E5E7EB",
    "&.selected": {
      border: "2px solid #111827",
      boxShadow: "0 0 0 2px rgba(17,24,39,0.1)",
    },
  },
  assetIcon: {
    width: 48,
    height: 48,
    backgroundColor: "#E5E7EB",
    borderRadius: "12px",
  },
  assetSymbol: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  assetBalance: {
    fontSize: 14,
    color: "#6B7280",
  },
  assetDetailSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "20px",
  },
  assetName: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
    paddingBottom: "8px",
  },
  assetMetricsRow: {
    display: "flex",
    justifyContent: "flex-start",
    gap: "65px",
    marginBottom: "24px",
  },
  metricLabel: {
    fontSize: "18px",
    color: "#6B7280",
    marginBottom: "8px",
  },
  metricValue: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
  },
  actionButtonsRow: {
    display: "flex",
    gap: "12px",
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: 600,
    color: "#6B7280",
    paddingLeft: 4,
  },
  txList: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    gap: 16,
  },
  txRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    alignItems: "center",
    fontSize: 14,
    color: "#374151",
  },
  txCategory: {
    fontWeight: 600,
    textTransform: "capitalize",
  },
  txAmountPositive: {
    justifySelf: "center",
    fontWeight: 600,
    color: "#10B981", // green
  },
  txAmountNegative: {
    justifySelf: "center",
    fontWeight: 600,
    color: "#EF4444", // red
  },
  txAmount: {
    justifySelf: "center",
    fontWeight: 600,
    color: "#10B981",
  },

  txDate: {
    justifySelf: "end",
    color: "#9CA3AF",
    fontSize: 13,
  },

  shimmer: {
    animation: "$shimmer 1.5s infinite linear",
    background: "linear-gradient(to right, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
    backgroundSize: "200% 100%",
  },
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "-200% 0" },
    "100%": { backgroundPosition: "200% 0" },
  },
  shimmerBox: {
    borderRadius: "8px",
    backgroundColor: "#F3F4F6",
  },
  balanceShimmer: {
    height: "72px",
    width: "180px",
    borderRadius: "8px",
  },
  assetCardShimmer: {
    height: 140,
    width: 160,
    borderRadius: "12px",
    backgroundColor: "#F3F4F6",
    flexShrink: 0,
  },
  txRowShimmer: {
    height: 20,
    borderRadius: 6,
    backgroundColor: "#F3F4F6",
    width: "100%",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    marginTop: "24px",
    marginBottom: "12px",
  },

  notificationBanner: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "16px 24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginTop: "16px",
  },

  bannerContent: {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#374151",
  },

  bannerLink: {
    color: "#374151",
    textDecoration: "underline",
    marginLeft: "4px",
    fontWeight: 500,
  },

  bannerTextContent: {
    flex: 1,
    marginLeft: "12px",
  },

  bannerTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
  },

  bannerSubtitle: {
    fontSize: "14px",
    color: "#6B7280",
  },

  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
  },

  assetIconSmall: {
    width: "36px",
    height: "36px",
  },

  rewardsCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "16px 24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },

  rewardsCardContent: {
    display: "flex",
    alignItems: "center",
  },

  rewardsTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
  },

  rewardsBalance: {
    fontSize: "14px",
    color: "#6B7280",
    textTransform: "uppercase",
  },

  offerCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "16px 24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },

  offerCardContent: {
    display: "flex",
    alignItems: "center",
  },

  offerTextContent: {
    flex: 1,
    marginLeft: "12px",
  },

  offerTitle: {
    fontSize: "16px",
    fontWeight: 500,
    color: "#111827",
  },

  offerSubtitle: {
    fontSize: "14px",
    color: "#6B7280",
  },
  distributionSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "20px",
  },
  distributionHeader: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    marginBottom: "16px",
    borderBottom: "1px solid #E5E7EB",
    paddingBottom: "8px",
  },
  distributionRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    padding: "12px 0",
    borderBottom: "1px solid #F3F4F6",
  },
  distributionDate: {
    fontSize: "14px",
    color: "#6B7280",
  },
  distributionAmount: {
    fontSize: "14px",
    color: "#111827",
  },
  distributionStatus: {
    fontSize: "14px",
    fontWeight: 600,
    textAlign: "right",
  },
  distributionStatusCompleted: {
    color: "#10B981",
  },
  distributionStatusPending: {
    color: "#F59E0B",
  },
  distributionEmpty: {
    padding: "16px 0",
    color: "#6B7280",
    fontSize: "14px",
    textAlign: "center",
  },
}));

const EARNING_RATES = {
  "USDE": "Up to 7% APY",
  "SOL": "Up to 5.5% APY",
  "WEETH": "3.8% APR",
  "WSTETH": "2.8% APR",
  "MINA": "Up to 9.74% APY",
  "AXL": "Up to 7% APY",
  "CSPR": "Up to 6.8% APY",
  "SUI": "Up to 2.5% APY",
};

const getUrlParam = (param: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

const setUrlParam = (param: string, value: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set(param, value);
  window.history.replaceState({}, '', url.toString());
};

const WalletPage: React.FC = () => {
  const classes = useStyles();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevSearchTerm = useRef("");
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [totalBalance, setTotalBalance] = useState<string | null>("$0");
  const [totalLux, setTotalLux] = useState<number | null>(null);
  const [geminiSubaccountPath, setGeminiSubaccountPath] = useState<string | null>(null);
  const [loansPath, setLoansPath] = useState<string | null>(null);
  const [lockedBalance, setLockedBalance] = useState<string | null>(null);
  const [distributionSchedule, setDistributionSchedule] = useState<Distribution[]>([]);
  const [dailyRewards, setDailyRewards] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [eligibilities, setEligibilities] = useState<Eligibilities | null>(null);

  const [isLoadingBalances, setIsLoadingBalances] = useState(true);
  const [isLoadingLedger, setIsLoadingLedger] = useState(true);
  const [isLoadingTotal, setIsLoadingTotal] = useState(true);
  const [isLoadingLux, setIsLoadingLux] = useState(false);
  const [isLoadingDistribution, setIsLoadingDistribution] = useState(false);
  const [isLoadingRewards, setIsLoadingRewards] = useState(false);
  const [isLoadingLockedBalance, setIsLoadingLockedBalance] = useState(false);
  const [isLoadingGeminiPath, setIsLoadingGeminiPath] = useState(false);
  const [isLoadingLoans, setIsLoadingLoans] = useState(false);
  const [isLoadingEligibilities, setIsLoadingEligibilities] = useState(false);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const scrollToSelectedAssetByIndex = (wallets: Wallet[], selectedWallet: Wallet) => {
    setTimeout(() => {
      if (scrollRef.current && selectedWallet) {
        const index = wallets.findIndex(w => w.id === selectedWallet.id);
        if (index !== -1) {
          const cards = scrollRef.current.querySelectorAll(`.${classes.assetCard}`);
          const targetCard = cards[index];
          if (targetCard) {
            targetCard.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }
      }
    }, 100);
  };

  useEffect(() => {
    const fetchWallets = async () => {
      setIsLoadingBalances(true);
      setIsLoadingTotal(true);
      try {
        const response = await axiosRequest("/fast_wallet/wallet_data", {}, RequestType.GET) as AxiosResponse<{ total_balance: string, wallets: Wallet[] }>;
        const wallets = response.data.wallets;
        wallets.forEach(wallet => {
          if (!wallet.asset.name?.trim()) {
            wallet.asset.name = wallet.asset.code;
          }
        });
        wallets.sort((a, b) => {
          if (a.balance === "0" && b.balance !== "0") return 1;
          if (b.balance === "0" && a.balance !== "0") return -1;
          return b.base_balance - a.base_balance;
        });
        setWallets(wallets);

        let assetParam = getUrlParam('asset');
        if (!assetParam) {
          const pathMatch = window.location.pathname.match(/\/wallets\/(.+)$/);
          if (pathMatch) {
            assetParam = pathMatch[1];
          }
        }

        if (assetParam) {
          const targetWallet = wallets.find(wallet =>
            wallet.asset.code.toLowerCase() === assetParam.toLowerCase() ||
            wallet.asset.teller_code.toLowerCase() === assetParam.toLowerCase()
          ) || wallets[0];
          scrollToSelectedAssetByIndex(wallets, targetWallet);
          setSelectedWallet(targetWallet);
        } else {
          setSelectedWallet(wallets[0]);
        }
        setTotalBalance(`$${Number(response.data.total_balance).toLocaleString()}`);
      } catch (err) {
      } finally {
        setIsLoadingBalances(false);
        setIsLoadingTotal(false);
      }
    };

    fetchWallets();
  }, []);

  const fetchTotalLux = async () => {
    setIsLoadingLux(true);
    try {
      const response = await axiosRequest("/fast_wallet/total_lux", {}, RequestType.GET) as AxiosResponse<{ total_lux: number }>;
      setTotalLux(response.data.total_lux);
    } catch (err) {
      setTotalLux(0);
    } finally {
      setIsLoadingLux(false);
    }
  };

  const fetchGeminiSubaccountPath = async () => {
    setIsLoadingGeminiPath(true);
    try {
      const response = await axiosRequest("/fast_wallet/gemini_subaccount_path", {}, RequestType.GET) as AxiosResponse<{ path: string | null }>;
      setGeminiSubaccountPath(response.data.path);
    } catch (err) {
      setGeminiSubaccountPath(null);
    } finally {
      setIsLoadingGeminiPath(false);
    }
  };

  const fetchDailyRewards = async (assetSymbol: string) => {
    setIsLoadingRewards(true);
    try {
      const response = await axiosRequest("/fast_wallet/daily_rewards", { asset_symbol: assetSymbol }, RequestType.GET) as AxiosResponse<{ daily_rewards: any[] }>;
      setDailyRewards(response.data.daily_rewards);
    } catch (err) {
      setDailyRewards([]);
    } finally {
      setIsLoadingRewards(false);
    }
  };

  const fetchAssetData = async (assetSymbol: string) => {
    setIsLoadingLedger(true);
    setIsLoadingDistribution(true);
    setIsLoadingLockedBalance(true);
    setIsLoadingLoans(true);
    setIsLoadingEligibilities(true);

    setLedgerEntries([]);
    setDistributionSchedule([]);
    setLockedBalance(null);
    setLoansPath(null);
    setEligibilities({ can_withdraw: false, can_deposit: false, can_trade: false });

    try {
      const response = await axiosRequest(
        `/fast_wallet/wallet_data/${assetSymbol}`,
        {},
        RequestType.GET
      ) as AxiosResponse<{
        ledger_entries: LedgerEntry[];
        distribution_schedule: Distribution[];
        locked_balance: { locked_balance: string | null };
        loans_path: { path: string | null };
        eligibilities: Eligibilities;
      }>;

      const data = response.data;

      setLedgerEntries(data.ledger_entries);
      setDistributionSchedule(data.distribution_schedule);
      setLockedBalance(data.locked_balance.locked_balance);
      setLoansPath(data.loans_path.path);
      setEligibilities(data.eligibilities);

    } catch (err) {
    } finally {
      setIsLoadingLedger(false);
      setIsLoadingDistribution(false);
      setIsLoadingLockedBalance(false);
      setIsLoadingLoans(false);
      setIsLoadingEligibilities(false);
    }
  }

  useEffect(() => {
    if (prevSearchTerm.current && !searchTerm && scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
    prevSearchTerm.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    if (selectedWallet) {
      const code = selectedWallet.asset.teller_code;
      setUrlParam('asset', code);

      fetchAssetData(code);

      if (code === "LBTC") {
        fetchTotalLux();
        fetchDailyRewards(code);
      }

      if (code === "FIL") {
        fetchGeminiSubaccountPath();
      }
    }
  }, [selectedWallet]);

  const filteredWallets = React.useMemo(() => {
    const newWallets = [...wallets]
      .filter((wallet) =>
        wallet.asset.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wallet.asset.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    if (searchTerm !== "") {
      setSelectedWallet(newWallets[0] || null);
    }
    return newWallets;
  }, [wallets, searchTerm]);

  const handleDeposit = () => {
    if (selectedWallet) {
      window.location.href = `/wallet_redirect/${selectedWallet.asset.teller_code}/deposit`;
    }
  };

  const handleWithdraw = () => {
    if (selectedWallet) {
      window.location.href = `/wallet_redirect/${selectedWallet.asset.teller_code}/withdraw`;
    }
  };

  const handleTrade = () => {
    if (selectedWallet) {
      window.location.href = `/wallet_redirect/${selectedWallet.asset.teller_code}/trade`;
    }
  };

  const selectedSymbol = selectedWallet?.asset.code || "";
  const precision = selectedSymbol === "USD" ? 2 : 4;

  const DistributionSchedule = () => {
    if (isLoadingDistribution) {
      return (
        <div className={classes.distributionSection}>
          <div className={classes.distributionHeader}>Distribution Schedule</div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className={`${classes.shimmer} ${classes.txRowShimmer}`} style={{ marginBottom: '10px' }} />
          ))}
        </div>
      );
    }

    if (!distributionSchedule.length) {
      return null;
    }

    return (
      <div>
        <div className={classes.assetName}>Distribution Schedule</div>
        <div className={classes.distributionSection}>
          {distributionSchedule.length > 0 ? (
            distributionSchedule.map((distribution) => (
              <div key={distribution.id} className={classes.distributionRow}>
                <div className={classes.distributionDate}>
                  {formatDate(distribution.vesting_date)}
                </div>
                <div className={classes.distributionAmount}>
                  {distribution.amount.cents} {distribution.amount.currency_iso}
                </div>
                <div className={`
                ${classes.distributionStatus} 
                ${distribution.state === "completed"
                    ? classes.distributionStatusCompleted
                    : classes.distributionStatusPending}
              `}>
                  {distribution.state === "completed" ? "Completed" : "Pending"}
                </div>
              </div>
            ))
          ) : (
            <div className={classes.distributionEmpty}>
              No distribution schedule available
            </div>
          )}
        </div>
      </div>);
  };

  const DailyRewards = () => {
    if (selectedWallet?.asset.code !== "LBTC") return null;

    if (isLoadingRewards) {
      return (
        <div className={classes.assetDetailSection} style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '16px' }}>Daily Rewards</div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className={`${classes.shimmer} ${classes.txRowShimmer}`} style={{ marginBottom: '10px' }} />
          ))}
        </div>
      );
    }

    if (dailyRewards === null) {
      return (
        <>
          <div className={classes.assetName}>Daily Rewards</div>
          <div className={classes.assetDetailSection} style={{ marginTop: '16px' }}>
            <div style={{
              padding: '16px 0',
              color: '#6B7280',
              fontSize: '14px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="icon-info-circled-alt" style={{ marginRight: '8px' }} />
              LBTC rewards data is temporarily unavailable for display. Please check back soon.
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={classes.assetName}>Daily Rewards</div>
        <div className={classes.assetDetailSection} style={{ marginTop: '16px' }}>

          <div className={classes.txRow} style={{ marginBottom: '16px', fontWeight: 600, fontSize: '12px', color: '#6B7280' }}>
            <div>Date</div>
            <div>Description</div>
            <div style={{ textAlign: 'right' }}>Amount</div>
          </div>

          {dailyRewards.map((reward, index) => (
            reward.reward_points > 0 && (
              <div key={index} className={classes.txRow} style={{ paddingTop: '16px', paddingBottom: '16px', borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ color: '#9CA3AF', fontSize: '14px' }}>
                  {new Date(reward.date_day).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
                <div style={{ color: '#9CA3AF', fontSize: '14px' }}>
                  Reward for holding {reward.currency}
                  {reward.balance_type === "pro" && ` (${reward.balance_type})`}
                </div>
                <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: 700, color: '#10B981' }}>
                  {reward.reward_points} {reward.reward_type}
                </div>
              </div>
            )
          ))}
        </div>
      </>
    );
  };

  const LendingRewards = () => {
    if (!selectedWallet || !loansPath) return null;

    if (isLoadingLoans) {
      return (
        <div className={classes.assetDetailSection} style={{ marginTop: '16px' }}>
          <div className={`${classes.shimmer} ${classes.shimmerBox}`} style={{ height: '60px' }} />
        </div>
      );
    }

    return (
      <div className={classes.assetDetailSection} style={{ marginTop: '16px' }}>
        <div style={{
          fontSize: '15px',
          fontWeight: 700,
          paddingBottom: '8px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center'
        }}>
          <i className="icon-gift" style={{
            fontSize: '18px',
            verticalAlign: 'middle',
            marginRight: '8px'
          }} />
          <span style={{ marginRight: '8px', verticalAlign: 'middle' }}>
            Lending Rewards
          </span>
          <span style={{ fontWeight: 400 }}>
            &middot;
          </span>
          <a
            href={loansPath}
            style={{
              marginLeft: '8px',
              fontWeight: 400,
              verticalAlign: 'middle',
              textDecoration: 'underline',
              color: '#374151'
            }}
          >
            View All Loans »
          </a>
        </div>
      </div>
    );
  };


  const InformationalMessage = ({ assetCode }) => {
    const classes = useStyles();

    if (assetCode === "FLOW") {
      return (
        <>
          <div className={classes.notificationBanner} style={{ marginBottom: '24px', marginTop: '8px' }}>
            <div className={classes.bannerContent} style={{ fontSize: '22px', alignItems: 'flex-start' }}>
              <i className="icon-attention" style={{ marginRight: '8px', color: '#F59E0B' }} />
              <span style={{ color: '#6B7280' }}>
                FLOW Delisting: Please withdraw or trade out of your FLOW tokens before June 30, 2025. After June 30, 2025, FLOW tokens will be sold and converted to USDC on your behalf.
              </span>
            </div>
          </div>
          <div style={{
            paddingBottom: '8px',
            paddingTop: '24px',
            color: '#9CA3AF',
            textAlign: 'center',
            fontSize: '14px'
          }}>
            <i className="icon-info-circled-alt" style={{ marginRight: '8px' }} />
            Please note that changes to your FLOW balance may take several hours to process
          </div>
        </>
      );
    }

    if (assetCode === "FIL") {
      return (
        <>
          <div className={classes.notificationBanner} style={{
            marginTop: '16px',
            backgroundColor: '#F9FAFB',
            fontSize: '15px'
          }}>
            <div className={classes.bannerContent} style={{ paddingLeft: '24px' }}>
              <i className="icon-clock" style={{ color: '#F59E0B', marginRight: '8px' }} />
              FIL deposits currently take about an hour to appear. Pending deposits will not appear here.
            </div>
          </div>

          {geminiSubaccountPath && (
            <div className={classes.notificationBanner} style={{
              marginTop: '16px',
              padding: '16px',
              textAlign: 'center',
              fontSize: '14px',
              color: '#6B7280'
            }}>
              <i className="icon-info-circled-alt" style={{ marginRight: '8px' }} />
              You now have a new FIL wallet with CoinList that has access to trading! &middot;{' '}
              <a
                href={geminiSubaccountPath}
                className={classes.bannerLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                View your old FIL Wallet »
              </a>
            </div>
          )}
        </>
      );
    }

    if (assetCode === "AXL") {
      return (
        <div style={{
          marginTop: '16px',
          textAlign: 'center',
          color: '#9CA3AF',
          fontSize: '14px'
        }}>
          <i className="icon-info-circled-alt" style={{ marginRight: '8px' }} />
          In the buy/sell page there is no option for the native AXL token, only the wrapped WAXL ERC20 token.
          This is because trading is currently only supported for the wrapped WAXL token.
          If you would like the native AXL token, you may purchase WAXL and convert it to WAXL via our conversions workflow.
        </div>
      );
    }

    return null;
  };

  const RewardsBanner = ({ assetCode, assetIconUrl }) => {
    const classes = useStyles();

    if (assetCode === "PEAQ") {
      return (
        <div className={classes.notificationBanner} style={{ marginBottom: '24px', marginTop: '8px' }}>
          <div className={classes.bannerContent} style={{ fontSize: '22px', alignItems: 'flex-start' }}>
            <i className="icon-attention" style={{ marginRight: '8px', color: '#F59E0B' }} />
            <span style={{ color: '#6B7280' }}>
              This is the native token of the PEAQ network, NOT the PEAQ token traded on the Ethereum network.
            </span>
          </div>
        </div>
      );
    }

    if (assetCode === "SOL") {
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <i className="icon-info-circled-alt" style={{ marginRight: 8 }} />
            <span>
              SOL automatically earns up to 5.5% APY when deposited into your CoinList Wallet.
              <a
                href="https://coinlist.co/help/information-on-sol-staking"
                className={classes.bannerLink}
              >
                Learn more
              </a>
            </span>
          </div>
        </div>
      );
    }

    if (assetCode === "USDE") {
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <i className="icon-info-circled-alt" style={{ marginRight: 8 }} />
            <span>
              Rewards for holding USDe will be automatically distributed in your wallet every Friday.
              <a
                href="https://blog.coinlist.co/earn-6-apy-with-ethenas-usde/"
                className={classes.bannerLink}
              >
                Learn more
              </a>
            </span>
          </div>
        </div>
      );
    }

    if (assetCode === "KING") {
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <i className="icon-info-circled-alt" style={{ marginRight: 8 }} />
            <span>
              KING Tokens will be automatically distributed in the coming weeks from holding weETH.
              <a
                href="https://blog.coinlist.co/introducing-weeth-wsteth-staking-on-coinlist/"
                className={classes.bannerLink}
              >
                Learn more
              </a>
            </span>
          </div>
        </div>
      );
    }

    if (assetCode === "BTC") {
      const lbtcWallet = wallets.find(w => w.asset.code === "LBTC");
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <div className={classes.iconWrapper}>
              <img
                src={lbtcWallet ? lbtcWallet.asset.icon_url : "/assets/tokens/LBTC.svg"}
                alt="LBTC"
                className={classes.assetIconSmall}
              />
            </div>
            <div className={classes.bannerTextContent}>
              <div className={classes.bannerTitle}>LBTC</div>
              <div className={classes.bannerSubtitle}>
                Earn Lombard Lux and Babylon points when you hodl LBTC
              </div>
            </div>
            <a href="/lombard" className={classes.lightBtn}>
              Learn More
            </a>
          </div>
        </div>
      );
    }

    if (assetCode === "LBTC") {
      return (
        <div className={classes.notificationBanner}>
          <div className={classes.bannerContent}>
            <div className={classes.iconWrapper}>
              <img
                src={assetIconUrl}
                alt="LBTC"
                className={classes.assetIconSmall}
              />
            </div>
            <div className={classes.bannerTextContent}>
              <div className={classes.bannerTitle}>LBTC</div>
              <div className={classes.bannerSubtitle}>
                Earn Lombard Lux and Babylon points when you hodl LBTC
              </div>
            </div>
            <a href="/lombard" className={classes.lightBtn}>
              Learn More
            </a>
          </div>
        </div>
      );
    }

    return null;
  };

  const OffersSection = ({ assetCode, assetIconUrl, wallets, onSelectWallet }) => {
    const classes = useStyles();

    if (assetCode === "WEETH") {
      // Find the KING wallet if it exists
      const kingWallet = wallets.find(w => w.asset.code === "KING");

      return (
        <>
          <div>
            <div className={classes.assetName}>Rewards</div>
            <div className={classes.rewardsCard} onClick={() => kingWallet && onSelectWallet(kingWallet)}>
              <div className={classes.rewardsCardContent}>
                <img
                  src={kingWallet ? kingWallet.asset.icon_url : "/assets/tokens/KING.svg"}
                  alt="KING"
                  className={classes.assetIcon}
                />
                <div className={classes.offerTextContent}>
                  <div className={classes.rewardsTitle}>KING Tokens</div>
                  <div className={classes.rewardsBalance}>
                    {kingWallet ? `${kingWallet.balance} KING` : "0 KING"}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className={classes.assetName}>Offers</div>
            <div className={classes.offerCard}>
              <div className={classes.offerCardContent}>
                <img
                  src={assetIconUrl}
                  alt="WEETH"
                  className={classes.assetIcon}
                />
                <div className={classes.offerTextContent}>
                  <div className={classes.offerTitle}>Introducing weETH Staking</div>
                  <div className={classes.offerSubtitle}>
                    Earn KING Protocol Tokens and yield when you hold weETH
                  </div>
                </div>
                <a
                  href="https://blog.coinlist.co/introducing-weeth-wsteth-staking-on-coinlist/"
                  className={classes.lightBtn}
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (assetCode === "KING") {
      const weethWallet = wallets.find(w => w.asset.code === "WEETH");

      return (
        <>
          <div className={classes.assetName}>Offers</div>
          <div className={classes.offerCard}>
            <div className={classes.offerCardContent}>
              <img
                src={weethWallet ? weethWallet.asset.icon_url : "/assets/tokens/WEETH.svg"}
                alt="WEETH"
                className={classes.assetIcon}
              />
              <div className={classes.offerTextContent}>
                <div className={classes.offerTitle}>Introducing weETH Staking</div>
                <div className={classes.offerSubtitle}>
                  Earn KING Protocol Tokens and yield when you hold weETH
                </div>
              </div>
              <a
                href="https://blog.coinlist.co/introducing-weeth-wsteth-staking-on-coinlist/"
                className={classes.lightBtn}
              >
                Learn More
              </a>
            </div>
          </div>
        </>
      );
    }

    if (assetCode === "USDE") {
      return (
        <>
          <div className={classes.assetName}>Offers</div>
          <div className={classes.offerCard}>
            <div className={classes.offerCardContent}>
              <img
                src={assetIconUrl}
                alt="USDE"
                className={classes.assetIcon}
              />
              <div className={classes.offerTextContent}>
                <div className={classes.offerTitle}>Hold Ethena USDe, Earn 7% APY</div>
                <div className={classes.offerSubtitle}>
                  Hold USDe on CoinList and earn up to 7% APY. No staking, no lockups.
                </div>
              </div>
              <a
                href="https://blog.coinlist.co/earn-6-apy-with-ethenas-usde/"
                className={classes.lightBtn}
              >
                Learn More
              </a>
            </div>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className={classes.page}>
      <div className={classes.container}>
        <div className={classes.balanceHeaderRow}>
          <div>
            {!isLoadingTotal && (
              <div className={classes.balanceLabel}>Your balance</div>
            )}
            {isLoadingTotal ? (
              <div className={`${classes.shimmer} ${classes.shimmerBox} ${classes.balanceShimmer}`} />
            ) : (
              <div className={classes.h1}>{totalBalance}</div>
            )}
          </div>
        </div>


        <input
          type="text"
          placeholder="Search assets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.searchBox}
        />

        <div className={classes.assetScrollWrapper}>
          <div className={classes.assetScroll} ref={scrollRef}>
            {isLoadingBalances
              ? [...Array(10)].map((_, i) => (
                <div key={i} className={`${classes.shimmer} ${classes.assetCardShimmer}`} />
              ))
              : filteredWallets.map((wallet) => (
                <div
                  key={wallet.id}
                  className={`${classes.assetCard} ${selectedSymbol === wallet.asset.code ? "selected" : ""}`}
                  onClick={() => setSelectedWallet(wallet)}
                >
                  <img className={classes.assetIcon} src={wallet.asset.icon_url} alt={wallet.asset.code} />
                  <div className={classes.assetSymbol}>{wallet.asset.code}</div>
                  {wallet.balance != "0" && < div className={classes.assetBalance}>${wallet.base_balance.toFixed(2)}</div>}
                </div>
              ))}
          </div>
        </div>

        {selectedWallet && (
          <div className={classes.assetDetailSection}>
            <div className={classes.assetName}>
              {selectedWallet.asset.name}
            </div>
            <div className={classes.assetMetricsRow}>
              <div>
                <div className={classes.metricLabel}>Available</div>
                <div className={classes.metricValue}>
                  {Number(selectedWallet?.balance || `0`).toFixed(precision)} {selectedSymbol}
                </div>
              </div>
              {lockedBalance && (
                <div>
                  <div className={classes.metricLabel}>Locked Balance</div>
                  <div className={classes.metricValue}>
                    {isLoadingLockedBalance ? (
                      <div className={`${classes.shimmer} ${classes.shimmerBox}`} style={{ height: '20px', width: '60px' }} />
                    ) : (
                      lockedBalance
                    )}
                  </div>
                </div>
              )}
              {selectedWallet.base_balance > 0 && (
                <div>
                  <div className={classes.metricLabel}>Value</div>
                  <div className={classes.metricValue}>
                    ${selectedWallet.base_balance.toFixed(2)}
                  </div>
                </div>
              )}
              {selectedWallet.asset.code === "LBTC" && (
                <div>
                  <div className={classes.metricLabel}>Total Lux</div>
                  <div className={classes.metricValue}>
                    {isLoadingLux ? (
                      <div className={`${classes.shimmer} ${classes.shimmerBox}`} style={{ height: '20px', width: '60px' }} />
                    ) : (
                      totalLux || 0
                    )}
                  </div>
                </div>
              )}
              {EARNING_RATES[selectedWallet?.asset.code] && (
                <div>
                  <div className={classes.metricLabel}>Earn</div>
                  <div className={classes.metricValue}>
                    {EARNING_RATES[selectedWallet?.asset.code]}
                  </div>
                </div>
              )}
            </div>
            <div className={classes.actionButtonsRow}>
              {selectedSymbol === 'USD' ? (
                <button
                  className={classes.actionBtn}
                  style={{ backgroundColor: 'rgb(89, 209, 16)' }}
                  onClick={() => window.location.href = '/conversions'}
                >
                  Convert to USDC
                </button>
              ) : (
                <>
                  {eligibilities?.can_trade && (
                    <button className={classes.actionBtn} onClick={handleTrade}>
                      Trade
                    </button>
                  )}
                  {eligibilities?.can_deposit && (
                    <button className={classes.lightBtn} onClick={handleDeposit}>
                      Add funds
                    </button>
                  )}
                  {eligibilities?.can_withdraw && (
                    <button className={classes.lightBtn} onClick={handleWithdraw}>
                      Withdraw
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {selectedWallet && <RewardsBanner assetCode={selectedWallet.asset.code} assetIconUrl={selectedWallet.asset.icon_url} />}

        {selectedWallet && <InformationalMessage assetCode={selectedWallet.asset.code} />}

        {selectedWallet && (
          <OffersSection
            assetCode={selectedWallet.asset.code}
            assetIconUrl={selectedWallet.asset.icon_url}
            wallets={wallets}
            onSelectWallet={(wallet: Wallet) => setSelectedWallet(wallet)}
          />
        )}

        {selectedWallet && (distributionSchedule || []).length > 0 && <DistributionSchedule />}

        {selectedWallet && <DailyRewards />}

        {selectedWallet && <LendingRewards />}

        <div>
          <div className={classes.assetName}>Recent Activity</div>

          <div className={classes.txList}>
            {(isLoadingBalances || isLoadingLedger)
              ? [...Array(3)].map((_, i) => (
                <div key={i} className={`${classes.shimmer} ${classes.txRowShimmer}`} />
              ))
              : (ledgerEntries || []).map((tx) => (
                <div key={tx.id} className={classes.txRow}>
                  <div>
                    {tx.description}
                    {tx.link && (
                      <span style={{ marginLeft: '8px' }}>
                        [<a
                          href={tx.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#374151',
                            textDecoration: 'underline',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          {tx.linkText}
                        </a>]
                      </span>
                    )}
                  </div>
                  <div className={tx.isPositive ? classes.txAmountPositive : classes.txAmountNegative}>
                    {tx.amount}
                  </div>
                  <div className={classes.txDate}>{tx.date}</div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div >
  );
};

export default WalletPage;
